import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { Badge, PRIORITY_STYLE, STATUS_STYLE, PRIORITIES, STATUSES } from "../../components/DeskLayout";
import { UserX, AlarmClockOff, CheckCircle2, Ticket, CircleUserRound, Users } from "lucide-react";

const TINT = {
  red: { card: "bg-red-50/90 border-red-200", label: "text-red-700", value: "text-red-900", icon: "bg-red-100 text-red-600" },
  amber: { card: "bg-amber-50/90 border-amber-200", label: "text-amber-800", value: "text-amber-900", icon: "bg-amber-100 text-amber-600" },
  cyan: { card: "bg-cyan-50/90 border-cyan-200", label: "text-cyan-800", value: "text-cyan-950", icon: "bg-cyan-100 text-cyan-700" },
  emerald: { card: "bg-emerald-50/90 border-emerald-200", label: "text-emerald-800", value: "text-emerald-950", icon: "bg-emerald-100 text-emerald-700" },
  slate: { card: "bg-white border-slate-200", label: "text-slate-500", value: "text-slate-900", icon: "bg-slate-100 text-slate-600" },
};

export const StatCard = ({ icon: Icon, label, value, tone = "slate", sub, testId }) => {
  const t = TINT[tone];
  return (
    <div data-testid={testId} className={`p-5 rounded-xl border transition-all duration-200 hover:shadow-md ${t.card}`}>
      <div className="flex items-center justify-between">
        <p className={`text-xs font-semibold uppercase tracking-wider ${t.label}`}>{label}</p>
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${t.icon}`}><Icon size={16} /></span>
      </div>
      <p className={`text-3xl font-bold font-heading mt-1 ${t.value}`}>{value}</p>
      {sub && <p className={`text-xs mt-0.5 ${t.label}`}>{sub}</p>}
    </div>
  );
};

export default function DeskDashboard() {
  const { user } = useAuth();
  const isAgent = user?.role !== "hd_user";
  const [data, setData] = useState(null);
  const [myTickets, setMyTickets] = useState([]);
  const [avail, setAvail] = useState(null);
  useEffect(() => {
    const h = (e) => { setAvail(e.detail); if (isAgent) api.get("/helpdesk/dashboard").then((r) => setData(r.data)).catch(() => {}); };
    window.addEventListener("hd-avail", h);
    return () => window.removeEventListener("hd-avail", h);
  }, [isAgent]);
  const load = () => {
    if (isAgent) api.get("/helpdesk/dashboard").then((r) => setData(r.data)).catch(() => {});
    else api.get("/helpdesk/tickets").then((r) => setMyTickets(r.data)).catch(() => {});
  };
  useEffect(load, [isAgent]); // eslint-disable-line react-hooks/exhaustive-deps

  const setPresence = async (next) => {
    try {
      await api.put("/helpdesk/availability", { available: next });
      setAvail(next);
      window.dispatchEvent(new CustomEvent("hd-avail", { detail: next }));
      toast.success(next ? "You're online — new assignments enabled" : "You're away — no auto-assignments");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!isAgent) {
    const open = myTickets.filter((t) => !["resolved", "closed"].includes(t.status));
    return (
      <div data-testid="desk-dashboard-user">
        <h1 className="text-2xl font-bold font-heading">Welcome, {user?.name}</h1>
        <p className="text-slate-500 text-sm mt-1 mb-6">You have {open.length} open ticket{open.length === 1 ? "" : "s"}.</p>
        <div className="flex gap-3 mb-8">
          <Link to="/desk/tickets" data-testid="user-new-ticket-cta" className="px-5 py-2.5 rounded-lg bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800">Open a ticket</Link>
          <Link to="/desk/kb" className="px-5 py-2.5 rounded-lg border border-slate-300 bg-white text-sm font-medium hover:bg-slate-50">Browse knowledge base</Link>
        </div>
        <TicketTable tickets={myTickets.slice(0, 10)} />
      </div>
    );
  }

  const online = avail ?? user?.hd_available !== false;
  const max = data ? Math.max(...Object.values(data.by_priority), 1) : 1;
  return (
    <div data-testid="desk-dashboard">
      <h1 className="text-2xl font-bold font-heading">Support Workspace</h1>
      <p className="text-sm text-slate-500 mt-1 mb-5">Queue overview with Advanced Work Assignment.</p>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="presence-bar">
        <div className="flex items-center gap-3">
          <span className={`w-2.5 h-2.5 rounded-full ${online ? "bg-emerald-500" : "bg-slate-300"}`} />
          <div>
            <p className="text-sm font-semibold" data-testid="presence-status">You're {online ? "online" : "away"}</p>
            <p className="text-xs text-slate-400">Workload {data?.my_open ?? 0} open ticket{(data?.my_open ?? 0) === 1 ? "" : "s"}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button data-testid="presence-online" onClick={() => setPresence(true)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors ${online ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-500 border-slate-300 hover:bg-slate-50"}`}>Online</button>
          <button data-testid="presence-away" onClick={() => setPresence(false)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors ${!online ? "bg-slate-700 text-white border-slate-700" : "bg-white text-slate-500 border-slate-300 hover:bg-slate-50"}`}>Away</button>
        </div>
      </div>

      {data?.agents?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5" data-testid="agents-on-shift">
          <p className="text-sm font-semibold flex items-center gap-2 mb-2"><Users size={15} className="text-slate-400" /> Agents on shift
            <span className="text-[11px] font-medium text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5">
              {data.agents.filter((a) => a.available).length} online · {data.agents.length} total
            </span>
          </p>
          <div className="flex flex-wrap gap-2">
            {data.agents.map((a) => (
              <div key={a.id} data-testid={`agent-shift-${a.id}`} className="flex items-center gap-2 border border-slate-100 rounded-lg px-3 py-2 min-w-[180px]">
                <span className={`w-2 h-2 rounded-full ${a.available ? "bg-emerald-500" : "bg-slate-300"}`} />
                <div>
                  <p className="text-sm font-medium leading-tight">{a.name}</p>
                  <p className="text-[11px] text-slate-400">{a.workload} open assigned</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <StatCard icon={AlarmClockOff} label="SLA breached" value={data?.sla_breached ?? "—"} tone="red" testId="stat-breached" />
        <StatCard icon={UserX} label="Unassigned" value={data?.unassigned ?? "—"} tone="amber" testId="stat-unassigned" />
        <StatCard icon={CircleUserRound} label="My open tickets" value={data?.my_open ?? "—"} tone="cyan" testId="stat-my-open" />
        <StatCard icon={Ticket} label="Open tickets" value={data?.open ?? "—"} tone="slate" testId="stat-open" />
        <StatCard icon={CheckCircle2} label="Resolved today" value={data?.resolved_today ?? "—"} tone="emerald" testId="stat-resolved" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-semibold font-heading text-sm mb-4">Open by priority</h3>
          {PRIORITIES.map((p) => (
            <div key={p} className="flex items-center gap-3 mb-2.5">
              <span className="w-8 text-xs font-semibold">{p}</span>
              <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${p === "P1" ? "bg-red-500" : p === "P2" ? "bg-orange-500" : p === "P3" ? "bg-amber-400" : "bg-slate-400"}`}
                  style={{ width: `${((data?.by_priority?.[p] || 0) / max) * 100}%` }} />
              </div>
              <span className="w-6 text-xs text-slate-500 text-right">{data?.by_priority?.[p] ?? 0}</span>
            </div>
          ))}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-semibold font-heading text-sm mb-4">All tickets by status</h3>
          <div className="flex flex-wrap gap-2">
            {STATUSES.map((s) => (
              <div key={s} className="border border-slate-200 rounded-xl px-4 py-3 text-center min-w-[90px]">
                <p className="text-xl font-bold font-heading">{data?.by_status?.[s] ?? 0}</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">{s.replace(/_/g, " ")}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <h3 className="font-semibold font-heading text-sm mb-3">Recent tickets</h3>
      <TicketTable tickets={data?.recent || []} />
    </div>
  );
}

export const TicketTable = ({ tickets }) => (
  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
    {tickets.length === 0 ? <p className="p-6 text-sm text-slate-400" data-testid="tickets-empty">No tickets yet.</p> : (
      <div className="overflow-x-auto"><table className="min-w-full divide-y divide-slate-200 text-sm text-left">
        <thead><tr className="bg-slate-50">
          {["Number", "Title", "Priority", "Status", "Requester", "Assignee", "Created"].map((h) => (
            <th key={h} className="px-5 py-3 font-medium text-slate-500 uppercase tracking-wider text-xs">{h}</th>))}
        </tr></thead>
        <tbody className="divide-y divide-slate-100">
          {tickets.map((t) => (
            <tr key={t.id} data-testid={`ticket-row-${t.id}`} className="hover:bg-slate-50">
              <td className="px-5 py-3"><Link to={`/desk/tickets/${t.id}`} className="font-semibold text-sky-700 hover:underline" data-testid={`ticket-link-${t.id}`}>{t.number}</Link></td>
              <td className="px-5 py-3 font-medium max-w-[260px] truncate">{t.title}</td>
              <td className="px-5 py-3"><Badge value={t.priority} map={PRIORITY_STYLE} /></td>
              <td className="px-5 py-3"><Badge value={t.status} map={STATUS_STYLE} /></td>
              <td className="px-5 py-3 text-slate-500">{t.requester_name}</td>
              <td className="px-5 py-3 text-slate-500">{t.assignee_name || <span className="text-slate-300">—</span>}</td>
              <td className="px-5 py-3 text-slate-400 whitespace-nowrap">{new Date(t.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
    )}
  </div>
);
